import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { CoverImage } from "../shared/CoverImage";
import { fullName } from "../../lib/format";
import PriorityOverrideModal from "./PriorityOverrideModal";
import MergeDepartmentModal from "./MergeDepartmentModal";
import AnnouncementsTab from "./AnnouncementsTab";
import LiveTab from "./translation/LiveTab";
import GlossaryTab from "./translation/GlossaryTab";
import {
  apiListDepartments,
  apiListPositions,
  apiListAssignments,
  apiAddDeptUser,
  apiBulkAddDeptUsers,
  apiRemoveDeptUser,
  apiCreatePosition,
  apiDeletePosition,
  apiCreateAssignment,
  apiDeleteAssignment,
  apiClearPriorityOverride,
  apiPriorityOverrideHistory,
  apiListAnnouncements,
  apiSetDeptUserPermission,
} from "./api";

// DepartmentDetailPage — full detail view for a single department.
// Tabs: Members / Positions / Announcements (Phase 3 stub).
// Super Admin can also set/clear a temporary priority override (Phase 2).
export default function DepartmentDetailPage({ currentUser, isSuperAdmin, isLimitedAdmin, isPastor, members, setMembers, users = [], navigate }) {
  const { id } = useParams();
  const isAdmin = isSuperAdmin || isLimitedAdmin;
  const [dept, setDept] = useState(null);
  const [positions, setPositions] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [tab, setTab] = useState("members"); // members | positions | announcements
  const [busy, setBusy] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkResult, setBulkResult] = useState(null); // { added: [], skipped: [] }
  const [addMemberSearch, setAddMemberSearch] = useState("");
  // Position-assign search mirrors the same live-search pattern as the
  // department-level "+ Add Member" panel — no more dropdowns.
  const [assignUserSearch, setAssignUserSearch] = useState("");
  const [newPosition, setNewPosition] = useState("");
  const [assignFor, setAssignFor] = useState(null); // position object
  const [assignForm, setAssignForm] = useState({ userId: "", type: "longterm", startDate: "", endDate: "" });
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [overrideHistory, setOverrideHistory] = useState([]);
  // Phase 3 — merge
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [allDepts, setAllDepts] = useState([]);
  const [announcementCount, setAnnouncementCount] = useState(0);

  const reload = useCallback(async () => {
    const list = await apiListDepartments();
    setAllDepts(Array.isArray(list) ? list : []);
    const d = (Array.isArray(list) ? list : []).find(x => x.id === id) || null;
    setDept(d);
    if (d) {
      // Announcements are now read-only-visible to every logged-in user; the
      // backend gates write actions (post/edit/delete). Always fetch so Team
      // Members visiting another dept's page see the basic announcements feed.
      const [p, a, anns] = await Promise.all([
        apiListPositions(id),
        apiListAssignments(id),
        apiListAnnouncements(id).catch(() => []),
      ]);
      setPositions(Array.isArray(p) ? p : []);
      setAssigns(Array.isArray(a) ? a : []);
      setAnnouncementCount(Array.isArray(anns) ? anns.length : 0);
    }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  if (!dept) return <div data-testid="dept-detail-loading" style={{ padding: 20, color: "var(--kc-sub)" }}>Loading…</div>;

  // ----- User-based dept memberships -----
  // dept.users now holds an array of user IDs. We resolve each user to a
  // display object (name + photo) via their linked member profile, falling
  // back to the user's own name and initials avatar.
  const userIdToDisplay = (uid) => {
    const u = (users || []).find(x => x.id === uid);
    if (!u) return { id: uid, name: uid, photo: null, username: uid, memberId: "", isLinked: false };
    const linkedMember = u.memberId ? members.find(m => m.id === u.memberId) : null;
    return {
      id: uid,
      userId: u.id,
      username: u.username,
      name: linkedMember ? fullName(linkedMember) : (u.name || u.username),
      photo: (linkedMember && linkedMember.photo) || u.photo || null,
      memberId: linkedMember ? linkedMember.id : "",
      isLinked: !!linkedMember,
    };
  };
  // Dedupe defensively — legacy department records can list the same userId
  // twice, which would otherwise cause a React duplicate-key warning when we
  // render each user row below.
  const deptUserIds = Array.from(new Set(Array.isArray(dept.users) ? dept.users : []));
  // Sort the resolved user displays alphabetically by their effective last
  // name (linked member's lastName when available, else the last word of
  // the user's display name).
  const _displayKey = (d) => {
    const m = d.memberId ? members.find(x => x.id === d.memberId) : null;
    if (m && m.lastName) return [(m.lastName || "").toLowerCase(), (m.firstName || "").toLowerCase()];
    const parts = String(d.name || "").trim().split(/\s+/);
    if (parts.length >= 2) return [parts[parts.length - 1].toLowerCase(), parts.slice(0, -1).join(" ").toLowerCase()];
    return [(d.name || "").toLowerCase(), ""];
  };
  const deptUserDisplays = deptUserIds.map(userIdToDisplay).sort((a, b) => {
    const [aL, aF] = _displayKey(a);
    const [bL, bF] = _displayKey(b);
    if (aL !== bL) return aL < bL ? -1 : 1;
    if (aF !== bF) return aF < bF ? -1 : 1;
    return 0;
  });
  const leader = dept.leaderUserId ? userIdToDisplay(dept.leaderUserId) : null;
  const isLeader = !!(dept.leaderUserId && dept.leaderUserId === currentUser.id);
  const canManage = isAdmin || isLeader || isPastor;
  const canManagePositions = isAdmin || isLeader;

  const addUser = async (userId) => {
    setBusy(true);
    try {
      await apiAddDeptUser(id, userId);
      await reload();
      setShowAddMember(false);
    } catch (e) { alert(e.message || "Could not add user"); }
    setBusy(false);
  };
  const submitBulkAdd = async () => {
    const userIds = Array.from(bulkSelected);
    if (userIds.length === 0) return;
    setBusy(true);
    try {
      const r = await apiBulkAddDeptUsers(id, userIds);
      setBulkResult({ added: r.added || [], skipped: r.skipped || [] });
      setBulkSelected(new Set());
      await reload();
    } catch (e) { alert(e.message || "Bulk add failed"); }
    setBusy(false);
  };
  const closeBulkAdd = () => {
    setShowBulkAdd(false);
    setBulkSearch("");
    setBulkSelected(new Set());
    setBulkResult(null);
  };
  const removeUser = async (userId) => {
    if (!window.confirm("Remove this user from the department?")) return;
    try {
      await apiRemoveDeptUser(id, userId);
      await reload();
    } catch (e) { alert(e.message || "Could not remove"); }
  };
  const addPosition = async () => {
    if (!newPosition.trim()) return;
    try {
      await apiCreatePosition(id, { name: newPosition.trim() });
      setNewPosition("");
      await reload();
    } catch (e) { alert(e.message || "Could not create"); }
  };
  const deletePosition = async (pid) => {
    if (!window.confirm("Delete this position and all its assignments?")) return;
    try { await apiDeletePosition(pid); await reload(); } catch (e) { alert(e.message); }
  };
  const submitAssign = async () => {
    if (!assignForm.userId) return;
    try {
      await apiCreateAssignment(assignFor.id, assignForm);
      setAssignFor(null);
      setAssignForm({ userId: "", type: "longterm", startDate: "", endDate: "" });
      await reload();
    } catch (e) { alert(e.message || "Could not assign"); }
  };
  const removeAssign = async (aid) => {
    if (!window.confirm("Remove this assignment?")) return;
    try { await apiDeleteAssignment(aid); await reload(); } catch (e) { alert(e.message); }
  };

  // ---- Priority override (Phase 2) ----
  const effectivePriority = dept.effectivePriority || dept.priority || "medium";
  const overrideActive = !!dept.overrideActive;
  const override = dept.priorityOverride || null;
  const onOverrideSaved = async () => {
    setShowOverrideModal(false);
    await reload();
  };
  const clearOverride = async () => {
    if (!window.confirm("Clear the active priority override now? This will immediately revert to the base priority and notify the leader and members.")) return;
    try {
      await apiClearPriorityOverride(id);
      await reload();
    } catch (e) { alert(e.message || "Could not clear override"); }
  };
  const loadHistory = async () => {
    try {
      const h = await apiPriorityOverrideHistory(id);
      setOverrideHistory(Array.isArray(h) ? h : []);
      setShowHistory(true);
    } catch (e) { alert(e.message || "Could not load history"); }
  };



  return (
    <div data-testid="dept-detail-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate("/departments")}>← All Departments</button>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {dept.isDonations && (
            <button
              className="btn btn-gold btn-sm"
              data-testid="dept-open-donations-workspace"
              onClick={() => navigate(`/donations/${id}`)}
              title="Open the Donations workspace (Intake Log, Distribution Log, Community Needs, Reports)"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            ><CustomIcon name="care-heart" size={14} /> Open Donations Workspace</button>
          )}
          {dept.isFinance && (
            <button
              className="btn btn-gold btn-sm"
              data-testid="dept-open-finance-workspace"
              onClick={() => navigate(`/finance`)}
              title="Open the Finance workspace (Giving, Expenses, Reports)"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            ><CustomIcon name="coin" size={14} /> Open Finance Workspace</button>
          )}
          {isSuperAdmin && allDepts.length > 1 && (
            <button
              className="btn btn-outline btn-sm"
              data-testid="dept-merge-btn"
              onClick={() => setShowMergeModal(true)}
              title="Merge this department into another"
            >⇆ Merge into…</button>
          )}
        </div>
      </div>
      <div className="card mb-20" style={{ padding: 0, overflow: "hidden" }} data-testid="dept-cover-wrap">
        <CoverImage
          value={dept.coverImage || ""}
          title={dept.name}
          subtitle={`${deptUserDisplays.length} member${deptUserDisplays.length === 1 ? "" : "s"}`}
          iconName="people-group"
          overlay={typeof dept.overlay === "number" ? dept.overlay : 55}
          textColor={dept.textColor || "white"}
          textEffect={dept.textEffect || "shadow"}
          data-testid="dept-cover-display"
        />
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", padding: 20 }}>
          <div className="dept-logo" style={{ width: 72, height: 72, fontSize: 32, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{dept.logoUrl ? <img src={dept.logoUrl} alt="" /> : <CustomIcon name="tools" size={40} />}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.05em" }} data-testid="dept-header-name">{dept.name}</div>
              <span className={`priority-badge ${effectivePriority}`} data-testid="dept-header-priority">{effectivePriority}</span>
              {overrideActive && (
                <span
                  data-testid="dept-override-badge"
                  title={override ? `Override active ${override.startDate} → ${override.endDate}` : ""}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "color-mix(in srgb, var(--kc-accent) 16%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 55%, transparent)",
                    color: "var(--kc-accent)", borderRadius: 999, padding: "3px 10px",
                    fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
                    fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase",
                  }}
                >
                  <span aria-hidden="true" style={{ display: "inline-flex" }}><CustomIcon name="bolt" size={11} /></span> Override Active
                </span>
              )}
            </div>
            {dept.description && <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-sub)" }}>{dept.description}</div>}
          </div>
        </div>
      </div>

      {/* Department Leader card — dedicated section below the header so it never overlays the name/icon/priority badge */}
      {leader && (
        <div
          className="card mb-20"
          data-testid="dept-leader-card"
          style={{ padding: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}
        >
          <Avatar name={leader.name} photo={leader.photo} memberId={leader.memberId} style={{ width: 48, height: 48, fontSize: 16, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: "var(--kc-accent)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Department Leader</div>
            <button
              type="button"
              data-testid={`dept-leader-link-${leader.id}`}
              onClick={() => leader.memberId && navigate(`/members/${leader.memberId}`)}
              disabled={!leader.memberId}
              style={{
                background: "transparent", border: "none", padding: 0, marginTop: 2,
                cursor: "pointer", color: "var(--kc-text)",
                fontFamily: "'Montserrat',sans-serif", fontWeight: 600, fontSize: 15,
                textAlign: "left", textDecoration: "none",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = "var(--kc-accent)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#FFFFFF"; }}
            >
              {leader.name}
            </button>
          </div>
        </div>
      )}

      {/* Phase 2 — Priority Override panel (visible to Super Admin always; banner visible to everyone when active) */}
      {(overrideActive || isSuperAdmin) && (
        <div
          className="card mb-20"
          data-testid="dept-override-panel"
          style={{
            padding: 16,
            borderColor: overrideActive ? "color-mix(in srgb, var(--kc-accent) 55%, transparent)" : undefined,
            background: overrideActive ? "linear-gradient(180deg,color-mix(in srgb, var(--kc-accent) 8%, transparent),transparent)" : undefined,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: "0.1em", color: "var(--kc-accent)", fontWeight: 800, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif", marginBottom: 6 }}>Priority Override</div>
              {overrideActive && override ? (
                <div data-testid="dept-override-summary" style={{ color: "var(--kc-text)", fontFamily: "'Montserrat',sans-serif", fontSize: 13, lineHeight: 1.5 }}>
                  Priority temporarily set to{" "}
                  <span className={`priority-badge ${override.priority}`} style={{ marginLeft: 2 }}>{override.priority}</span>{" "}
                  from <strong>{override.startDate}</strong> to <strong>{override.endDate}</strong>
                  {override.reason ? <> — <em style={{ opacity: 0.85 }}>“{override.reason}”</em></> : null}
                  <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>
                    Set by {override.setByName || "—"} · base priority is{" "}
                    <span className={`priority-badge ${dept.priority || "medium"}`}>{dept.priority || "medium"}</span>
                  </div>
                </div>
              ) : (
                <div style={{ color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif", fontSize: 12 }}>
                  No override active. Base priority: <span className={`priority-badge ${dept.priority || "medium"}`}>{dept.priority || "medium"}</span>
                </div>
              )}
            </div>
            {isSuperAdmin && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className="btn btn-sm btn-outline"
                  data-testid="dept-override-history-btn"
                  onClick={loadHistory}
                >
                  History
                </button>
                {overrideActive ? (
                  <>
                    <button
                      className="btn btn-sm btn-outline"
                      data-testid="dept-override-edit-btn"
                      onClick={() => setShowOverrideModal(true)}
                    >
                      Edit Override
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      data-testid="dept-override-clear-btn"
                      onClick={clearOverride}
                    >
                      Clear Override
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-sm btn-gold"
                    data-testid="dept-override-set-btn"
                    onClick={() => setShowOverrideModal(true)}
                  >
                    Set Override
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="checkins-tabs">
        <button type="button" data-testid="dept-tab-members" className={`checkins-tab ${tab === "members" ? "active" : ""}`} onClick={() => setTab("members")}>Members ({deptUserDisplays.length})</button>
        <button type="button" data-testid="dept-tab-positions" className={`checkins-tab ${tab === "positions" ? "active" : ""}`} onClick={() => setTab("positions")}>Positions ({positions.length})</button>
        <button type="button" data-testid="dept-tab-announcements" className={`checkins-tab ${tab === "announcements" ? "active" : ""}`} onClick={() => setTab("announcements")}>Announcements</button>
        {dept.deptType === "translation" && (
          <>
            <button type="button" data-testid="dept-tab-live" className={`checkins-tab ${tab === "live" ? "active" : ""}`} onClick={() => setTab("live")}>Live</button>
            <button type="button" data-testid="dept-tab-glossary" className={`checkins-tab ${tab === "glossary" ? "active" : ""}`} onClick={() => setTab("glossary")}>Glossary</button>
          </>
        )}
      </div>

      {tab === "members" && (
        <div data-testid="dept-members-list">
          {canManage && (
            <div style={{ marginBottom: 12 }}>
              {showAddMember ? (
                <div className="card" data-testid="dept-add-member-panel" style={{ padding: 12 }}>
                  <div className="form-group">
                    <label htmlFor="dept-add-user-search">Add user to {dept.name}</label>
                    <input
                      id="dept-add-user-search"
                      type="search"
                      data-testid="dept-add-member-search"
                      autoFocus
                      autoComplete="off"
                      placeholder="Search by username or display name…"
                      value={addMemberSearch}
                      onChange={e => setAddMemberSearch(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                  {addMemberSearch.trim() && (
                    <div data-testid="dept-add-member-results" style={{ maxHeight: 220, overflowY: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 8, marginTop: 8 }}>
                      {users
                        .filter(u => !deptUserIds.includes(u.id))
                        .map(u => ({ ...userIdToDisplay(u.id) }))
                        .filter(d => {
                          const q = addMemberSearch.trim().toLowerCase();
                          return d.name.toLowerCase().includes(q) || (d.username || "").toLowerCase().includes(q);
                        })
                        .slice(0, 25)
                        .map(d => (
                          <button
                            key={d.id}
                            type="button"
                            data-testid={`dept-add-member-result-${d.id}`}
                            onClick={() => { addUser(d.id); setAddMemberSearch(""); }}
                            disabled={busy}
                            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", color: "var(--kc-text)", border: 0, borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}
                          >
                            <Avatar name={d.name} photo={d.photo} memberId={d.memberId} style={{ width: 28, height: 28, fontSize: 10 }} />
                            <span style={{ display: "flex", flexDirection: "column" }}>
                              <span>{d.name}</span>
                              <span style={{ fontSize: 10, color: "var(--kc-meta)" }}>@{d.username}</span>
                            </span>
                          </button>
                        ))}
                      {users.filter(u => !deptUserIds.includes(u.id)).map(u => userIdToDisplay(u.id)).filter(d => { const q = addMemberSearch.trim().toLowerCase(); return d.name.toLowerCase().includes(q) || (d.username || "").toLowerCase().includes(q); }).length === 0 && (
                        <div style={{ padding: "10px 12px", color: "var(--kc-meta)", fontSize: 12 }}>No matches.</div>
                      )}
                    </div>
                  )}
                  <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={() => { setShowAddMember(false); setAddMemberSearch(""); }}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btn-gold btn-sm" data-testid="dept-add-member-btn" onClick={() => setShowAddMember(true)}>+ Add User</button>
                  <button className="btn btn-outline btn-sm" data-testid="dept-bulk-add-btn" onClick={() => setShowBulkAdd(true)}>+ Bulk Add</button>
                </div>
              )}
            </div>
          )}
          {deptUserDisplays.length === 0 ? (
            <div className="widget-empty">No users in this department yet.</div>
          ) : (
            <div>
              {deptUserDisplays.map(d => {
                const memberAssigns = assigns.filter(a => a.userId === d.id);
                return (
                  <div key={d.id} className="task-row" data-testid={`dept-member-${d.id}`} style={{ alignItems: "flex-start" }}>
                    <Avatar name={d.name} photo={d.photo} memberId={d.memberId} style={{ width: 36, height: 36, fontSize: 12 }} />
                    <div className="task-main" style={{ minWidth: 0 }}>
                      <div className="task-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span>{d.name}</span>
                        {dept.leaderUserId === d.id && <span className="priority-badge high">Leader</span>}
                        {!d.isLinked && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 999, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)", color: "var(--kc-sub)" }}>@{d.username}</span>}
                      </div>
                      <div className="task-meta">
                        {memberAssigns.length === 0
                          ? <span>No positions assigned</span>
                          : memberAssigns.map(a => (
                            <span key={a.id} className={`tm-pill ${a.type === "temporary" ? "repeat" : ""}`}>
                              {a.positionName} · {a.type === "temporary" ? `Temp ${a.startDate || ""}→${a.endDate || ""}` : "Long-term"}
                            </span>
                          ))}
                      </div>
                    </div>
                    {canManage && (
                      <button className="btn btn-sm btn-danger" data-testid={`dept-remove-member-${d.id}`} onClick={() => removeUser(d.id)}>Remove</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Team permission section — Finance-only, visible to leader + SA */}
          {(String(dept.name || "").trim().toLowerCase() === "finance") && (isAdmin || isLeader) && deptUserDisplays.length > 0 && (
            <FinanceTeamPermissions
              dept={dept}
              members={deptUserDisplays}
              onSaved={reload}
            />
          )}
        </div>
      )}

      {tab === "positions" && (
        <div data-testid="dept-positions-list">
          {canManagePositions && (
            <div className="card" style={{ padding: 12, marginBottom: 12 }}>
              <div className="flex gap-8" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
                <div className="form-group" style={{ flex: "1 1 200px", margin: 0 }}>
                  <label>New Position</label>
                  <input value={newPosition} onChange={e => setNewPosition(e.target.value)} placeholder="e.g. Vocalist, Sound Tech" data-testid="dept-new-position-input" />
                </div>
                <button className="btn btn-gold btn-sm" data-testid="dept-new-position-btn" onClick={addPosition} disabled={!newPosition.trim()}>+ Add</button>
              </div>
            </div>
          )}
          {positions.length === 0 ? (
            <div className="widget-empty">No positions defined yet.</div>
          ) : (
            <div>
              {positions.map(p => {
                const holders = assigns.filter(a => a.positionId === p.id);
                return (
                  <div key={p.id} className="card" data-testid={`dept-position-${p.id}`} style={{ marginBottom: 10, padding: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 600, color: "var(--kc-text)" }}>{p.name}</div>
                        {p.description && <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>{p.description}</div>}
                      </div>
                      {canManagePositions && (
                        <>
                          <button className="btn btn-sm btn-outline" data-testid={`dept-assign-${p.id}`} onClick={() => { setAssignFor(p); setAssignForm({ memberId: "", type: "longterm", startDate: "", endDate: "" }); }}>+ Assign</button>
                          <button className="btn btn-sm btn-danger" data-testid={`dept-position-delete-${p.id}`} onClick={() => deletePosition(p.id)}>Del</button>
                        </>
                      )}
                    </div>
                    {holders.length > 0 && (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                        {holders.map(a => {
                          const d = userIdToDisplay(a.userId);
                          return (
                            <div key={a.id} className="flex items-center gap-8" style={{ background: "var(--kc-surface)", padding: "8px 10px", borderRadius: 8 }}>
                              <Avatar name={d.name} photo={d.photo} memberId={d.memberId} style={{ width: 24, height: 24, fontSize: 10 }} />
                              <span style={{ flex: 1, fontSize: 12, color: "var(--kc-text)" }}>{d.name}</span>
                              <span className={`tm-pill ${a.type === "temporary" ? "repeat" : ""}`}>{a.type === "temporary" ? `Temp ${a.startDate}→${a.endDate}` : "Long-term"}</span>
                              {canManagePositions && <button className="btn btn-sm btn-danger" data-testid={`dept-assignment-delete-${a.id}`} onClick={() => removeAssign(a.id)} aria-label="Remove assignment"><CustomIcon name="close" size={12} /></button>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "announcements" && (
        <AnnouncementsTab
          deptId={id}
          deptName={dept.name}
          currentUser={currentUser}
          canPost={canManage}
          deptMemberCount={deptUserDisplays.length}
          onChanged={reload}
        />
      )}

      {tab === "live" && dept.deptType === "translation" && (
        <LiveTab deptId={id} />
      )}

      {tab === "glossary" && dept.deptType === "translation" && (
        <GlossaryTab deptId={id} canEdit={canManage || dept.leaderUserId === currentUser?.id} />
      )}

      {assignFor && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAssignFor(null)}>
          <div className="modal" data-testid="dept-assign-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Assign &ldquo;{assignFor.name}&rdquo;</div>
              <button className="modal-close" onClick={() => setAssignFor(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group mb-14">
                <label htmlFor="dept-assign-user-search">User *</label>
                {assignForm.userId ? (
                  <div data-testid="dept-assign-member-selected" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 8 }}>
                    <Avatar name={userIdToDisplay(assignForm.userId).name} photo={userIdToDisplay(assignForm.userId).photo} memberId={userIdToDisplay(assignForm.userId).memberId} style={{ width: 24, height: 24, fontSize: 10 }} />
                    <span style={{ flex: 1, fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "var(--kc-text)" }}>
                      {userIdToDisplay(assignForm.userId).name}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      data-testid="dept-assign-member-clear"
                      onClick={() => { setAssignForm(f => ({ ...f, userId: "" })); setAssignUserSearch(""); }}
                    >Change</button>
                  </div>
                ) : (
                  <>
                    <input
                      id="dept-assign-user-search"
                      type="search"
                      data-testid="dept-assign-member-search"
                      autoComplete="off"
                      placeholder="Start typing a name or username…"
                      value={assignUserSearch}
                      onChange={e => setAssignUserSearch(e.target.value)}
                    />
                    {assignUserSearch.trim() && (
                      <div data-testid="dept-assign-member-results" style={{ maxHeight: 180, overflowY: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 8, marginTop: 6 }}>
                        {deptUserDisplays
                          .filter(d => {
                            const q = assignUserSearch.trim().toLowerCase();
                            return d.name.toLowerCase().includes(q) || (d.username || "").toLowerCase().includes(q);
                          })
                          .slice(0, 25)
                          .map(d => (
                            <button
                              key={d.id}
                              type="button"
                              data-testid={`dept-assign-member-result-${d.id}`}
                              onClick={() => { setAssignForm(f => ({ ...f, userId: d.id })); setAssignUserSearch(""); }}
                              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", color: "var(--kc-text)", border: 0, borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}
                            >
                              <Avatar name={d.name} photo={d.photo} memberId={d.memberId} style={{ width: 24, height: 24, fontSize: 10 }} />
                              <span style={{ display: "flex", flexDirection: "column" }}>
                                <span>{d.name}</span>
                                <span style={{ fontSize: 10, color: "var(--kc-meta)" }}>@{d.username}</span>
                              </span>
                            </button>
                          ))}
                        {deptUserDisplays.filter(d => { const q = assignUserSearch.trim().toLowerCase(); return d.name.toLowerCase().includes(q) || (d.username || "").toLowerCase().includes(q); }).length === 0 && (
                          <div style={{ padding: "10px 12px", color: "var(--kc-meta)", fontSize: 12 }}>No matches.</div>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div style={{ fontSize: 11, color: "var(--kc-meta)", marginTop: 4 }}>Only users already in this department can be assigned a position.</div>
              </div>
              <div className="form-group mb-14">
                <label>Assignment Type</label>
                <select data-testid="dept-assign-type" value={assignForm.type} onChange={e => setAssignForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="longterm">Long-term</option>
                  <option value="temporary">Temporary</option>
                </select>
              </div>
              {assignForm.type === "temporary" && (
                <div className="form-grid">
                  <div className="form-group"><label>Start Date *</label><input type="date" data-testid="dept-assign-start" value={assignForm.startDate} onChange={e => setAssignForm(f => ({ ...f, startDate: e.target.value }))} /></div>
                  <div className="form-group"><label>End Date *</label><input type="date" data-testid="dept-assign-end" value={assignForm.endDate} onChange={e => setAssignForm(f => ({ ...f, endDate: e.target.value }))} /></div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAssignFor(null)}>Cancel</button>
              <button className="btn btn-gold" data-testid="dept-assign-save" disabled={!assignForm.userId || (assignForm.type === "temporary" && (!assignForm.startDate || !assignForm.endDate))} onClick={submitAssign}>Assign</button>
            </div>
          </div>
        </div>
      )}

      {showOverrideModal && (
        <PriorityOverrideModal
          dept={dept}
          onSaved={onOverrideSaved}
          onClose={() => setShowOverrideModal(false)}
        />
      )}

      {showHistory && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowHistory(false)}>
          <div className="modal" data-testid="dept-override-history-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Priority Override History — {dept.name}</div>
              <button className="modal-close" onClick={() => setShowHistory(false)}>×</button>
            </div>
            <div className="modal-body">
              {overrideHistory.length === 0 ? (
                <div style={{ color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif", fontSize: 13, padding: 8 }}>
                  No prior overrides recorded.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {overrideHistory.slice().reverse().map((h, i) => (
                    <div
                      key={`oh-${i}-${h.setAt || ""}`}
                      data-testid={`dept-override-history-row-${i}`}
                      className="card"
                      style={{ padding: 12 }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <span className={`priority-badge ${h.priority}`}>{h.priority}</span>
                        <span style={{ fontSize: 12, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
                          {h.startDate} → {h.endDate}
                        </span>
                        <span style={{
                          marginLeft: "auto",
                          fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700,
                          padding: "2px 8px", borderRadius: 999,
                          background: h.expiredReason === "auto" ? "rgba(255,255,255,0.06)" : (h.expiredReason === "manual" ? "rgba(220,90,90,0.14)" : "color-mix(in srgb, var(--kc-accent) 14%, transparent)"),
                          color: h.expiredReason === "auto" ? "rgba(255,255,255,0.55)" : (h.expiredReason === "manual" ? "#E58E8E" : "var(--kc-accent)"),
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}>
                          {h.expiredReason || "ended"}
                        </span>
                      </div>
                      {h.reason && <div style={{ fontSize: 12, color: "var(--kc-sub)", fontStyle: "italic", marginBottom: 4 }}>“{h.reason}”</div>}
                      <div style={{ fontSize: 11, color: "var(--kc-meta)" }}>
                        Set by {h.setByName || "—"} · ended {h.expiredAt ? new Date(h.expiredAt).toLocaleString() : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowHistory(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showMergeModal && (
        <MergeDepartmentModal
          source={dept}
          candidates={allDepts.filter(d => d.id !== id)}
          memberCount={deptUserDisplays.length}
          positionCount={positions.length}
          assignmentCount={assigns.length}
          announcementCount={announcementCount}
          onClose={() => setShowMergeModal(false)}
          onMerged={() => {
            setShowMergeModal(false);
            // Source was deleted; navigate back to the list.
            navigate("/departments");
          }}
        />
      )}

      {showBulkAdd && (() => {
        const eligible = users
          .filter(u => !deptUserIds.includes(u.id))
          .map(u => userIdToDisplay(u.id));
        const q = bulkSearch.trim().toLowerCase();
        const filtered = q
          ? eligible.filter(d => d.name.toLowerCase().includes(q) || (d.username || "").toLowerCase().includes(q))
          : eligible;
        const allFilteredSelected = filtered.length > 0 && filtered.every(d => bulkSelected.has(d.id));
        const toggle = (uid) => {
          setBulkSelected(prev => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
          });
        };
        const toggleAllFiltered = () => {
          setBulkSelected(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
              filtered.forEach(d => next.delete(d.id));
            } else {
              filtered.forEach(d => next.add(d.id));
            }
            return next;
          });
        };
        return (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeBulkAdd()}>
            <div className="modal" data-testid="dept-bulk-add-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Bulk Add Users to {dept.name}</div>
                <button className="modal-close" onClick={closeBulkAdd}>×</button>
              </div>
              <div className="modal-body">
                {bulkResult ? (
                  <div data-testid="dept-bulk-add-result" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {bulkResult.added.length > 0 && (
                      <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.40)", color: "#4ade80", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                        <CustomIcon name="check" size={14} /> Added <strong>{bulkResult.added.length}</strong> user{bulkResult.added.length === 1 ? "" : "s"} to {dept.name}.
                      </div>
                    )}
                    {bulkResult.skipped.length > 0 && (
                      <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(255,155,107,0.10)", border: "1px solid rgba(255,155,107,0.40)", color: "#ff9b6b", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                        <CustomIcon name="warning" size={14} /> Skipped <strong>{bulkResult.skipped.length}</strong>: {bulkResult.skipped.map(s => `${s.userId} (${s.reason})`).join(", ")}
                      </div>
                    )}
                    {bulkResult.added.length === 0 && bulkResult.skipped.length === 0 && (
                      <div style={{ color: "var(--kc-sub)", fontSize: 13 }}>Nothing was added.</div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="form-group" style={{ marginBottom: 10 }}>
                      <label htmlFor="dept-bulk-search">Find users not yet in this department</label>
                      <input
                        id="dept-bulk-search"
                        type="search"
                        data-testid="dept-bulk-search"
                        autoComplete="off"
                        autoFocus
                        placeholder="Search by username or display name…"
                        value={bulkSearch}
                        onChange={e => setBulkSearch(e.target.value)}
                      />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: "var(--kc-sub)" }}>
                        {filtered.length} match{filtered.length === 1 ? "" : "es"} · {bulkSelected.size} selected
                      </span>
                      {filtered.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          data-testid="dept-bulk-toggle-all"
                          onClick={toggleAllFiltered}
                        >{allFilteredSelected ? "Clear all visible" : "Select all visible"}</button>
                      )}
                    </div>
                    <div data-testid="dept-bulk-results" style={{ maxHeight: 320, overflowY: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 8 }}>
                      {filtered.length === 0 ? (
                        <div style={{ padding: "14px 12px", color: "var(--kc-sub)", fontSize: 12, textAlign: "center" }}>
                          {eligible.length === 0 ? "All users are already in this department." : "No matches."}
                        </div>
                      ) : filtered.map(d => {
                        const checked = bulkSelected.has(d.id);
                        return (
                          <label
                            key={d.id}
                            data-testid={`dept-bulk-row-${d.id}`}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", background: checked ? "color-mix(in srgb, var(--kc-accent) 8%, transparent)" : "transparent" }}
                          >
                            <input
                              type="checkbox"
                              data-testid={`dept-bulk-check-${d.id}`}
                              checked={checked}
                              onChange={() => toggle(d.id)}
                              style={{ width: 16, height: 16, flexShrink: 0 }}
                            />
                            <Avatar name={d.name} photo={d.photo} memberId={d.memberId} style={{ width: 28, height: 28, fontSize: 10, flexShrink: 0 }} />
                            <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                              <span style={{ fontSize: 13, color: "var(--kc-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                              <span style={{ fontSize: 10, color: "var(--kc-meta)" }}>@{d.username}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {bulkResult ? (
                  <>
                    <button className="btn btn-outline" data-testid="dept-bulk-add-again" onClick={() => setBulkResult(null)}>Add more</button>
                    <button className="btn btn-gold" data-testid="dept-bulk-add-done" onClick={closeBulkAdd}>Done</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-outline" data-testid="dept-bulk-add-cancel" onClick={closeBulkAdd}>Cancel</button>
                    <button
                      className="btn btn-gold"
                      data-testid="dept-bulk-add-save"
                      disabled={bulkSelected.size === 0 || busy}
                      onClick={submitBulkAdd}
                    >{busy ? "Adding…" : `Add ${bulkSelected.size} user${bulkSelected.size === 1 ? "" : "s"}`}</button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ============================================================
// FinanceTeamPermissions — per-member Edit Access ↔ View Only
// toggle for the Finance department. Visible only to the
// Finance Leader and Super/Limited Admin (parent gates rendering).
// Excludes the leader themselves from the list — the DL always
// has full write access by definition.
// ============================================================
function FinanceTeamPermissions({ dept, members, onSaved }) {
  const perms = dept.userPermissions || {};
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");

  const leaderId = dept.leaderUserId;
  const teamMembers = members.filter(m => m.id !== leaderId);

  const setPerm = async (userId, perm) => {
    setSavingId(userId);
    setError("");
    try {
      await apiSetDeptUserPermission(dept.id, userId, perm);
      onSaved && onSaved();
    } catch (e) {
      setError(e?.message || "Could not update permission");
    } finally { setSavingId(""); }
  };

  if (teamMembers.length === 0) return null;

  return (
    <div
      className="card"
      data-testid="finance-team-permissions"
      style={{
        marginTop: 16, padding: 18,
        background: "linear-gradient(135deg, color-mix(in srgb, var(--kc-accent) 10%, transparent), color-mix(in srgb, var(--kc-accent) 3%, transparent))",
        border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <CustomIcon name="settings" size={16} />
        <div style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase", fontSize: 14 }}>
          Team Permissions
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--kc-sub)", marginBottom: 14, lineHeight: 1.4 }}>
        Grant each team member either <b>Edit Access</b> (create / edit giving + submit expenses) or <b>View Only</b> (read-only across every Finance tab).
        The Finance Leader always has full write access.
      </div>

      {teamMembers.map(m => {
        const raw = perms[m.id];
        // Support legacy string shape + new audit-object shape.
        const cur = typeof raw === "string" ? raw : (raw?.permission || "edit");
        const changedBy   = typeof raw === "object" ? raw?.changedByName : "";
        const changedDate = typeof raw === "object" && raw?.changedAt ? raw.changedAt.slice(0, 10) : "";
        const busy = savingId === m.id;
        return (
          <div
            key={m.id}
            data-testid={`finance-perm-row-${m.id}`}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 8px",
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Avatar name={m.name} photo={m.photo} memberId={m.memberId} style={{ width: 30, height: 30, fontSize: 12 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</div>
              {changedBy && changedDate && (
                <div
                  data-testid={`finance-perm-audit-${m.id}`}
                  style={{ fontSize: 10, opacity: 0.55, marginTop: 2 }}
                >
                  Changed by {changedBy} on {changedDate}
                </div>
              )}
            </div>
            <div style={{ display: "inline-flex", gap: 4, background: "rgba(0,0,0,0.30)", padding: 3, borderRadius: 999, border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)" }}>
              {[
                { key: "edit", label: "Edit" },
                { key: "view", label: "View Only" },
              ].map(opt => {
                const active = cur === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={busy || active}
                    onClick={() => setPerm(m.id, opt.key)}
                    data-testid={`finance-perm-${m.id}-${opt.key}`}
                    style={{
                      padding: "4px 12px", borderRadius: 999,
                      border: "none",
                      background: active ? "var(--kc-accent)" : "transparent",
                      color: active ? "var(--kc-on-accent)" : "var(--kc-sub)",
                      fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                      cursor: busy || active ? "default" : "pointer",
                      opacity: busy ? 0.6 : 1,
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
          </div>
        );
      })}

      {error && (
        <div data-testid="finance-perm-error" style={{ marginTop: 10, background: "rgba(248,113,113,0.15)", color: "#f87171", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}

